// Personel › Hasta İletişimi — telefon görüşmeleri: günlük eğilim, personel
// kırılımı ve en çok görüşülen hastalar (design-principles §4).
import React, { useEffect } from "react";
import { Box, Typography, alpha } from "@mui/material";
import {
  Phone as PhoneIcon,
  TrendingUp as TrendIcon,
  PeopleAlt as PeopleIcon,
  SupportAgent as CallerIcon,
} from "@mui/icons-material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";
import { useLazyGetPerformanceCommunicationQuery } from "../../store/dashboardApi";
import { useClinicTheme } from "../../theme";
import { COLORS, TYPO, CARD_GAP } from "../../styles/uiConstants";
import KpiTile from "../../components/molecules/KpiTile";
import {
  AnalyticsCard,
  RankBarList,
  BodySkeleton,
  CardEmpty,
  ChartTip,
  AXIS_TICK,
  GRID,
  nf,
  pct,
} from "../../components/organisms/charts/analyticsParts";
import {
  PerformanceTabFrame,
  usePreviewState,
  kpiGridSx,
  scrollAreaSx,
} from "./performanceTabParts";
import useTerms from "../../hooks/useTerms";

/**
 * Personel kırılımı: görüşme sayısı çubukta (tek seri, tema rengi); geri arama
 * sayısı metinle yazılır — iki değer renge değil etikete bağlı.
 */
function CallerList({ rows, color }) {
  const max = Math.max(0, ...rows.map((r) => r.count || 0));
  return (
    <Box sx={{ ...scrollAreaSx, display: "flex", flexDirection: "column", gap: 1.75 }}>
      {rows.map((r, idx) => (
        <Box key={`${r.caller}-${idx}`} sx={{ flexShrink: 0 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mb: 0.5 }}>
            <Typography noWrap sx={{ fontSize: TYPO.body, color: COLORS.slate700, minWidth: 0 }}>
              {r.caller || "Belirtilmedi"}
            </Typography>
            <Typography
              sx={{
                fontSize: TYPO.body,
                color: COLORS.slate500,
                fontVariantNumeric: "tabular-nums",
                flexShrink: 0,
              }}
            >
              <Box component="span" sx={{ fontWeight: 600, color: COLORS.slate800 }}>
                {nf(r.count)}
              </Box>
              {" görüşme"}
              {r.callbacks ? ` · ${nf(r.callbacks)} geri arama` : ""}
            </Typography>
          </Box>
          <Box sx={{ height: 8, borderRadius: 4, bgcolor: COLORS.slate100, overflow: "hidden" }}>
            <Box
              sx={{
                height: "100%",
                width: `${max ? Math.max(3, ((r.count || 0) / max) * 100) : 0}%`,
                borderRadius: 4,
                bgcolor: color,
              }}
            />
          </Box>
        </Box>
      ))}
    </Box>
  );
}

export default function CommunicationTab({ token, period, onPeriodChange }) {
  const terms = useTerms();
  const [fetch, { data, isLoading, isFetching }] = useLazyGetPerformanceCommunicationQuery();
  const { palette } = useClinicTheme();
  const preview = usePreviewState();

  useEffect(() => {
    if (token) fetch({ token, period });
  }, [token, period, fetch]);

  const loading = preview === "yukleniyor" || (!preview && (isLoading || isFetching || !data));
  const result = preview ? {} : data?.data || {};
  const phoneCalls = result.phoneCalls || {};
  const byCaller = result.byCaller || [];
  const topPatients = result.topPatients || [];
  const dailyTrend = result.dailyTrend || [];

  const callTotal = phoneCalls.total || 0;
  const callbacks = phoneCalls.callbacks || 0;
  const trendTotal = dailyTrend.reduce((s, d) => s + (d.count || 0), 0);
  const dailyAvg = dailyTrend.length ? trendTotal / dailyTrend.length : 0;
  const topCaller = byCaller[0]?.caller;
  const primary = palette.primary;

  return (
    <PerformanceTabFrame
      period={period}
      onPeriodChange={onPeriodChange}
      note={`${terms.patientLower} telefon görüşmeleri`}
    >
      <Box sx={kpiGridSx}>
        <KpiTile
          loading={loading}
          label="Görüşme"
          value={nf(callTotal)}
          hint="Bu dönemde kaydedilen telefon görüşmesi"
        />
        <KpiTile
          loading={loading}
          label="Geri arama"
          value={nf(callbacks)}
          hint={callTotal ? `Geri arama oranı %${nf(phoneCalls.callbackRate ?? pct(callbacks, callTotal))}` : "Bu dönemde görüşme yok"}
        />
        <KpiTile
          loading={loading}
          label="Günlük ortalama"
          value={nf(Math.round(dailyAvg * 10) / 10)}
          hint="Son 30 günde, gün başına görüşme"
        />
        <KpiTile
          loading={loading}
          label="Görüşen personel"
          value={nf(byCaller.length)}
          hint={topCaller ? `En çok görüşen: ${topCaller}` : "Bu dönemde görüşme yok"}
        />
      </Box>

      <AnalyticsCard
        title="Günlük görüşme"
        subtitle="Son 30 gün · dönem seçiminden bağımsız"
        icon={<TrendIcon />}
        bodyHeight={300}
      >
        {loading ? (
          <BodySkeleton kind="months" />
        ) : !trendTotal ? (
          <CardEmpty
            icon={<TrendIcon />}
            title="Son 30 günde görüşme yok"
            description={`${terms.patient} görüşmeleri kaydedildikçe günlük sayılar burada görünür.`}
          />
        ) : (
          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={dailyTrend}
                barCategoryGap="25%"
                margin={{ top: 8, right: 4, left: -16, bottom: 0 }}
              >
                <CartesianGrid {...GRID} />
                <XAxis
                  dataKey="label"
                  tick={AXIS_TICK}
                  axisLine={{ stroke: COLORS.slate200 }}
                  tickLine={false}
                  interval={4}
                />
                <YAxis allowDecimals={false} tick={AXIS_TICK} axisLine={false} tickLine={false} />
                <Tooltip
                  cursor={{ fill: alpha(COLORS.slate200, 0.4) }}
                  content={<ChartTip format={(p) => `${nf(p.value)} görüşme`} />}
                />
                <Bar dataKey="count" name="Görüşme" fill={primary} maxBarSize={18} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </AnalyticsCard>

      <Box
        sx={{
          display: "grid",
          gap: CARD_GAP,
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
        }}
      >
        <AnalyticsCard
          title="Personel"
          subtitle="Görüşme ve geri arama sayısı"
          icon={<CallerIcon />}
          bodyHeight={320}
        >
          {loading ? (
            <BodySkeleton kind="list" />
          ) : !byCaller.length ? (
            <CardEmpty
              icon={<PhoneIcon />}
              title="Bu dönemde görüşme yok"
              description={`Personel ${terms.patientLower} görüşmesi kaydettikçe kırılım burada görünür.`}
            />
          ) : (
            <CallerList rows={byCaller} color={primary} />
          )}
        </AnalyticsCard>

        <AnalyticsCard
          title={`En çok görüşülen ${terms.patientsLower}`}
          subtitle="Bu dönemde, görüşme sayısına göre"
          icon={<PeopleIcon />}
          bodyHeight={320}
        >
          {loading ? (
            <BodySkeleton kind="list" />
          ) : (
            <RankBarList
              items={topPatients.map((p, idx) => ({
                name: [p.name, p.surname].filter(Boolean).join(" ") || `Bilinmeyen ${terms.patientLower} ${idx + 1}`,
                count: p.count ?? 0,
              }))}
              color={primary}
              total={callTotal}
              unit="görüşme"
              emptyText={{
                icon: <PeopleIcon />,
                title: `Bu dönemde ${terms.patientLower} görüşmesi yok`,
                description: `${terms.patients}la yapılan görüşmeler kaydedildikçe burada sıralanır.`,
              }}
            />
          )}
        </AnalyticsCard>
      </Box>
    </PerformanceTabFrame>
  );
}
